import {
	Component,
	OnInit,
	ElementRef,
	ViewChildren,
	QueryList,
	ViewChild,
	AfterViewInit,
	Renderer2,
	OnDestroy
} from '@angular/core';
import { Store } from '@ngxs/store';
import { BroadcastService } from '@cv-invision/core';

import { QualityState } from '../state/quality.state';
import { DOCUMENT_RULES_UI_SECTIONS, SCHEMA_TYPE } from '../model/quality.constant';
import { HISTORICAL_VALUE_EVENT } from '../historical-values/historical-values.component';
import { RuleModel } from '../rules/model/rule.model';
import { RULE_MESSAGE_TYPE, SELECTED_RULE_CLASS } from './model/revised-fields.model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OrderDetailModel } from '../state/model/order-detail.model';
import { RulesState } from '../rules/state/rules.state';
import { RuleStateModel } from '../rules/state/model/rules-state.model';
import { RuleEngine } from '../rules/model/rule-engine';
import { OnRevised, DeleteSelectedRule } from '../rules/state/action/rules.actions';
import {RevisedFieldModel} from '../model/revised-fields.model';

@Component({
	selector: 'cv-revised-fields',
	templateUrl: './revised-fields.component.html',
	styleUrls: ['./revised-fields.component.scss']
})
export class RevisedFieldsComponent implements OnInit, AfterViewInit, OnDestroy {
	revised$: Observable<RevisedFieldModel> = this.store.select(QualityState.getRevisedFields);
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	rulesCompare$: Observable<Array<string>> = this.store.select(RulesState.getRuleCompareInfo);
	private atComponentDestroy = new Subject();
	failed: any;
	changes: any;
	schema: string;
	orderId: number;
	lastElementSelect: number;
	currentValues: Array<any>;
	scrollT: number;
	UISECTION: Array<any> = [
		{ UI: 'Overview' },
		{ UI: 'Subject' },
		{ UI: 'Improvements' },
		{ UI: 'Neighborhood' },
		{ UI: 'PhotoReview' },
		{ UI: 'SalesApproach' },
		{ UI: 'CostApproach' },
		{ UI: 'IncomeApproach' },
		{ UI: 'Reconciliation' },
		{ UI: 'Comps' }
	];
	@ViewChildren('changesEl') btnChanges: QueryList<ElementRef>;
	@ViewChildren('revisedFailed') revisedFailed: QueryList<ElementRef>;
	@ViewChild('contentRevised', { static: true }) contentRevised: ElementRef<any>;
	selectedRule$: Observable<any> = this.store.select(RulesState.getSelectedRule);
	constructor(
		private broadcastService: BroadcastService,
		private renderer: Renderer2,
		private router: Router,
		private store: Store
	) {}

	ngOnInit(): void {
		RuleEngine.setSelectedSection(this.store, DOCUMENT_RULES_UI_SECTIONS.REVISIONS);

		this.store.dispatch(new OnRevised(true));
		this.orderDetail$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((orderDetailModel: OrderDetailModel) => {
				if (orderDetailModel && orderDetailModel.id) {
					const { id, schema } = orderDetailModel;
					this.schema = schema;
					this.orderId = id;
				}
			});

		this.revised$.pipe(takeUntil(this.atComponentDestroy)).subscribe(revisedFields => {
			if (revisedFields) {
				this.failed = this.mapRevisedFieldData(revisedFields.failed);
				this.changes = this.mapRevisedFieldData(revisedFields.otherChanges);
			}
		});
	}

	/**
	 * get the selected rule info
	 * @param rule
	 */
	getSelectedRule(rule: RuleModel): void {
		if (this.failed) {
			const filterType = rule.targets && rule.targets.length ? rule.targets : rule.primaryTargets;
			const matchRule = this.failed.filter(p => filterType.includes(p.path));
			const rejectedEle = this.revisedFailed.toArray();
			const elm = rejectedEle.filter(r =>
				matchRule.some(m => m.path === r.nativeElement.attributes.path.value)
			);
			this.setRuleSelectedClass(elm, rejectedEle, rule.messageType);
		}
	}

	/**
	 * set class for the type of rule selected
	 * @param el
	 * @param rejectedEle
	 * @param msType
	 */
	setRuleSelectedClass(
		el: Array<ElementRef>,
		rejectedEle: Array<ElementRef>,
		msType: string
	): void {
		rejectedEle.forEach(e => {
			this.renderer.removeClass(e.nativeElement, SELECTED_RULE_CLASS.INFO);
			this.renderer.removeClass(e.nativeElement, SELECTED_RULE_CLASS.WARNING);
			this.renderer.removeClass(e.nativeElement, SELECTED_RULE_CLASS.ERROR);
		});
		if (el.length) {
			el.forEach(ele => {
				if (msType === RULE_MESSAGE_TYPE.WARNING) {
					this.renderer.addClass(ele.nativeElement, SELECTED_RULE_CLASS.WARNING);
				}
				if (msType === RULE_MESSAGE_TYPE.ERROR) {
					this.renderer.addClass(ele.nativeElement, SELECTED_RULE_CLASS.ERROR);
				}
				if (msType === RULE_MESSAGE_TYPE.INFO) {
					this.renderer.addClass(ele.nativeElement, SELECTED_RULE_CLASS.INFO);
				}
			});
			el[0].nativeElement.scrollIntoView(
				window.innerHeight < 750 ? false : { behavior: 'smooth', block: 'start' }
			);
		}
	}

	/**
	 * get scroll event
	 * @param $event
	 */
	onScroll(event: any): void {
		const target = event.target as Element;
		const isHistoricalCmp = target.classList.contains('cv-historical-values__container');

		if (!isHistoricalCmp) {
			const { scrollTop } = event.target;
			this.scrollT = scrollTop;
			this.broadcastService.broadcast({
				name: HISTORICAL_VALUE_EVENT,
				event: {
					isOpen: false,
					data: [],
					pos: {}
				}
			});
		}
	}

	/**
	 * check if a ui section is valid to redirect or not
	 * @param uiSection
	 */
	canRedirect(uiSection: string): boolean {
		if (!uiSection) {
			return false;
		}
		const hasValidUi = this.UISECTION.filter(u => u.UI === uiSection.toString());
		if (hasValidUi.length) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * redirect to ui section
	 * @param uiSection
	 */
	goToPage(uiSection: string): void {
		this.router.navigate([`/intelligence/quality/${uiSection.toLowerCase()}`, this.orderId]);
	}

	/**
	 * open historical component
	 * @param item
	 * @param historical
	 */
	historicalVal(historical: Array<any>, ele: any): void {
		this.currentValues = Object.assign([], historical);
		if (this.currentValues.length > 1) {
			this.currentValues.pop();
		}

		this.lastElementSelect = ele;
		const el = this.btnChanges.filter(item => {
			return item.nativeElement === ele;
		});
		const { offsetHeight, offsetLeft, offsetTop, offsetWidth } = el[0].nativeElement;
		const scrollTop = this.scrollT;
		this.broadcastService.broadcast({
			name: HISTORICAL_VALUE_EVENT,
			event: {
				isOpen: true,
				data: this.currentValues,
				pos: { offsetHeight, offsetLeft: offsetLeft + 80, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * map revised field
	 */
	mapRevisedFieldData(info: any): any {
		if (!Object.getOwnPropertyNames(info).length) {
			return null;
		}
		const data = Object.assign({}, info);
		return Object.keys(data).map(m => {
			if (this.schema === SCHEMA_TYPE.INSPECTION_R) {
				const pth = data[m].path.split('.');
				if (pth[pth.length - 1].includes('cv_photos')) {
					const metadata = [
						{
							DisplayText: pth[pth.length - 2]
						}
					];
					const value = 'cv_photos'.replace('_', ' ');
					return {
						...data[m],
						metadata,
						value
					};
				}
			}

			return data[m];
		});
	}

	ngAfterViewInit(): void {
		const el = this.contentRevised.nativeElement;
		// why this instead of hotlistener, scroll event dont work so well with that
		if (el) {
			el.addEventListener('scroll', this.onScroll.bind(this), true);
		}
		// After a rule has been selected if it belongs to the same section it will try to Highlight the Field
		this.selectedRule$.pipe(takeUntil(this.atComponentDestroy)).subscribe((selectedRule: any) => {
			if (selectedRule) {
				setTimeout(() => {
					this.getSelectedRule(selectedRule);
					// We Clean latest value
					this.store.dispatch(new DeleteSelectedRule());
				});
			}
		});
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.store.dispatch(new OnRevised(false));
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
